import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';
import App from './App.jsx';
import ErrorBoundary from './components/common/ErrorBoundary';
import GlobalErrorFallback from './components/common/GlobalErrorFallback';
import { initializeSentry } from './config/sentry.config';
import './index.scss';

/**
 * Initialize Sentry for error tracking and monitoring
 * Only initializes in production or when explicitly enabled in development
 */
initializeSentry({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  // Adjust sample rates based on environment
  tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
  replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 0.5,
  replaysOnErrorSampleRate: 1.0,
});

/**
 * Handle global unhandled promise rejections
 * @param {PromiseRejectionEvent} event - Promise rejection event
 */
const handleUnhandledRejection = (event) => {
  console.error('Unhandled promise rejection:', event.reason);

  // Prevent the default browser behavior (logging to console)
  event.preventDefault();

  // Capture in Sentry
  if (event.reason instanceof Error) {
    Sentry.captureException(event.reason, {
      tags: {
        type: 'unhandled_rejection',
      },
    });
  } else {
    Sentry.captureMessage('Unhandled promise rejection', {
      level: 'error',
      extra: {
        reason: event.reason,
      },
    });
  }
};

/**
 * Handle global errors
 * @param {ErrorEvent} event - Error event
 */
const handleGlobalError = (event) => {
  console.error('Global error:', event.error);

  // Capture in Sentry
  if (event.error) {
    Sentry.captureException(event.error, {
      tags: {
        type: 'global_error',
      },
      extra: {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  }
};

// Add global error handlers
window.addEventListener('unhandledrejection', handleUnhandledRejection);
window.addEventListener('error', handleGlobalError);

// Clean up listeners on page unload
window.addEventListener('beforeunload', () => {
  window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  window.removeEventListener('error', handleGlobalError);
});

/**
 * Wrap the App component with Sentry's ErrorBoundary
 * This provides an additional layer of error catching at the Sentry level
 */
const SentryWrappedApp = Sentry.withErrorBoundary(App, {
  fallback: ({ error, resetError }) => (
    <GlobalErrorFallback error={error} onReset={resetError} />
  ),
  showDialog: false, // We handle user feedback manually in our components
});

/**
 * Render the application with nested error boundaries
 * - Outer: Custom ErrorBoundary for UI control
 * - Inner: Sentry-wrapped App for automatic error reporting
 */
createRoot(document.getElementById('root')).render(
  <ErrorBoundary
    fallback={<GlobalErrorFallback />}
    onError={(error, errorInfo) => {
      // Top-level error handling
      console.error('Top-level application error:', error, errorInfo);

      // Capture in Sentry with additional context
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
        tags: {
          errorBoundary: 'root',
        },
      });
    }}
    enableSentry={true}
  >
    <SentryWrappedApp />
    <Toaster
      position="top-right"
      richColors
      toastOptions={{
        className: 'custom-toast',
      }}
    />
  </ErrorBoundary>
);
